import { buildFormValidate } from "@turo/libraries/lib/helpers/validationHelpers";
import { PhoneNumberInput, PrimaryButton, Select } from "@turo/pedal";
import type { CountryResponse } from "@turo/types-dunlop";
import { Loader } from "@turo/ui";
import type { CountryCode } from "libphonenumber-js/mobile";
import { type FormEventHandler, useEffect, useState } from "react";

import { OTPForm } from "~/src/components/OTPForm";
import { COUNTRIES_API_ENDPOINT } from "~/src/constants/api";
import { OTP_METHODS } from "~/src/constants/otp";
import { onShowOtpPhoneLoginStep } from "~/src/helpers/formEventActions";
import { dispatchLoginFailure } from "~/src/helpers/loginActions";
import { getRecaptchaToken } from "~/src/helpers/recaptcha";
import { OTP_TRACKING_EVENT, trackOtp } from "~/src/helpers/tracking";
import { useSimpleTranslate } from "~/src/SimpleTranslateProvider";
import type { StateFormField } from "~/types/stateForm";

import stateForm, { FIELD_TYPES } from "../../components/stateForm";
import { get, getPropertiesFromQuery } from "../../helpers/iframeHelpers";
import { type ClientRequestError, generateOTP } from "../../helpers/otpHelpers";
import {
  getLocaleCountry,
  getPhoneNumberPrefixFromCountryCode,
  getSupportedCountries,
  validateCountryCode,
  validatePhoneNumber,
} from "../../helpers/phoneNumberHelpers";
import type { OtpLoginPhoneTranslations } from ".";
import type { OnLoginFormSubmitSuccessOptions } from "./PhoneLoginView";

interface CountryCodeFormField extends StateFormField<HTMLSelectElement> {
  value: CountryCode | "";
}

/**
 * TODO-TS: `stateForm` props should be derived from `stateForm`
 */
interface PhoneLoginOTPFormProps {
  buildSubmitHandler: (
    onSubmit: (formData: {
      countryCode: CountryCode;
      phoneNumber: string;
    }) => Promise<void>,
  ) => FormEventHandler<HTMLFormElement>;
  formFields: {
    countryCode: CountryCodeFormField;
    phoneNumber: StateFormField;
  };
  isLoading: boolean;
  onLoginFormSubmitSuccess: (data: OnLoginFormSubmitSuccessOptions) => void;
  otpTokenLoginStart: () => void;
}

const COUNTRY_CODE_INVALID_ERROR_KEY =
  "auth.login.otp.phone.form.country_code.invalid_error";

const PHONE_NUMBER_INVALID_ERROR_KEY =
  "auth.login.otp.phone.form.phone_number.invalid_error";

const PhoneLoginOTPForm = ({
  buildSubmitHandler,
  formFields: { countryCode, phoneNumber },
  isLoading,
  onLoginFormSubmitSuccess,
  otpTokenLoginStart,
}: PhoneLoginOTPFormProps) => {
  const translate = useSimpleTranslate<OtpLoginPhoneTranslations>();
  const [formError, setFormError] = useState<string>();
  const [loadingCountryDataError, setIsLoadingCountryDataError] =
    useState<string>();
  const [isLoadingCountryData, setIsLoadingCountryData] = useState(true);
  const [countryData, setCountryData] = useState<CountryResponse[]>([]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- `useEffect` return types are distinct from returning promises
    get<CountryResponse[], ClientRequestError>(COUNTRIES_API_ENDPOINT).then(
      (data) => {
        setIsLoadingCountryData(false);

        if ("error" in data) {
          return setIsLoadingCountryDataError(data.errors[0].message);
        }

        return setCountryData(data);
      },
    );
  }, []);

  useEffect(() => {
    onShowOtpPhoneLoginStep();
  }, []);

  const supportedCountryOptions = getSupportedCountries(countryData);

  const handleSubmitTelephone = buildSubmitHandler(async (formData) => {
    otpTokenLoginStart();
    trackOtp(OTP_TRACKING_EVENT.PASSCODE_CONTINUE_CLICK);

    const recaptchaTokenOrError = await getRecaptchaToken({ translate });

    const data =
      typeof recaptchaTokenOrError === "string"
        ? await generateOTP({
            body: {
              ...formData,
              method: OTP_METHODS.SMS,
            },
            recaptchaToken: recaptchaTokenOrError,
          })
        : recaptchaTokenOrError;

    if ("error" in data) {
      dispatchLoginFailure(data);
      return setFormError(data.errors[0].message ?? undefined);
    }

    return onLoginFormSubmitSuccess({
      ...formData,
      otpToken: data.token,
    });
  });

  if (isLoadingCountryData) {
    return <Loader isLoading />;
  }

  /**
   * We return a specific translation in the `stateForm` `validate` helper.
   * If there is not a UI validation error present, we want to display the
   * error from the API, which by default is `undefined`.
   */
  const getCountryCodeInputSupportingText = () => {
    return countryCode.error === COUNTRY_CODE_INVALID_ERROR_KEY
      ? translate(countryCode.error)
      : loadingCountryDataError;
  };

  /** If there is an error present, `"error"` gives the component error indicators */
  const getCountryCodeInputStatus = () => {
    return !!countryCode.error || loadingCountryDataError ? "error" : undefined;
  };

  /**
   * We return a specific translation in the `stateForm` `validate` helper.
   * If there is not a UI validation error present, we want to display the
   * error from the API submission, which by default is `undefined`.
   *
   * If there are no errors present, we display the regular helper text.
   */
  const getPhoneNumberInputSupportingText = () => {
    return phoneNumber.error === PHONE_NUMBER_INVALID_ERROR_KEY
      ? translate(phoneNumber.error)
      : formError ||
          translate("auth.login.otp.phone.form.phone_number.helper_text");
  };

  /** If there is an error present, `"error"` gives the component error indicators */
  const getPhoneNumberInputStatus = () => {
    return phoneNumber.error || formError ? "error" : undefined;
  };

  return (
    <OTPForm onSubmit={handleSubmitTelephone}>
      <Select
        id="countryCode"
        label={translate("auth.login.otp.phone.form.country_code.label")}
        name="countryCode"
        onChange={countryCode.onChange}
        options={supportedCountryOptions}
        prefix={getPhoneNumberPrefixFromCountryCode(countryCode.value)}
        status={getCountryCodeInputStatus()}
        supportingText={getCountryCodeInputSupportingText()}
        value={countryCode.value}
      />
      <PhoneNumberInput
        id="phoneNumber"
        label={translate("auth.login.otp.phone.form.phone_number.label")}
        name="phoneNumber"
        onBlur={phoneNumber.onBlur}
        onChange={phoneNumber.onChange}
        onInput={phoneNumber.onBlur}
        placeholder={translate(
          "auth.login.otp.phone.form.phone_number.placeholder",
        )}
        status={getPhoneNumberInputStatus()}
        supportingText={getPhoneNumberInputSupportingText()}
        value={phoneNumber.value}
      />
      <PrimaryButton isDisabled={isLoading} isLoading={isLoading} type="submit">
        {translate("auth.login.otp.phone.form.continue.button_label")}
      </PrimaryButton>
    </OTPForm>
  );
};

const { countryCode, locale, phoneNumber } = getPropertiesFromQuery();
const countryLocale = getLocaleCountry(locale);

export default stateForm(PhoneLoginOTPForm, {
  initialValues: {
    countryCode: countryCode ?? countryLocale,
    phoneNumber: phoneNumber ?? "",
  },
  types: {
    countryCode: FIELD_TYPES.TEXT,
    phoneNumber: FIELD_TYPES.TELEPHONE,
  },
  /**
   * TODO: Add validation for inputs.
   *
   * @see https://team-turo.atlassian.net/browse/SCHU-13521
   */
  validate: buildFormValidate({
    countryCode: (countryCode: CountryCode) => {
      return validateCountryCode(countryCode)
        ? undefined
        : COUNTRY_CODE_INVALID_ERROR_KEY;
    },
    // @ts-expect-error -- TODO-TS: See above code comment
    phoneNumber: (
      phoneNumber: string,
      formData: { countryCode: CountryCode },
    ) => {
      return validatePhoneNumber({
        phoneNumber,
        countryCode: formData.countryCode,
      })
        ? undefined
        : PHONE_NUMBER_INVALID_ERROR_KEY;
    },
  }),
});
